import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, BarChart3, CreditCard, Activity, TrendingUp, UserPlus, DollarSign, AlertCircle, Clock, ChevronRight } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import toast from 'react-hot-toast';
import { apiFetch, apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';
import { AdminConfirmDialog, AdminPageHeader } from '../../components/AdminUi';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmRepair, setConfirmRepair] = useState(false);

  useEffect(() => { fetchStats(); }, []); // eslint-disable-line

  const fetchStats = async () => {
    try {
      setStats(await apiGet(API_ROUTES.admin.stats));
    } catch {
      toast.error('Failed to load admin stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
      </div>
    );
  }

  const repairEaNames = async () => {
    setConfirmRepair(false);
    try {
      toast.loading('Repairing EA names...', { id: 'repair' });
      const res = await apiFetch(`${API_ROUTES.admin.root}/repair-ea-names`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      toast.success(`Fixed ${data.tradesFixed} trades, ${data.statsFixed} EA stats`, { id: 'repair', duration: 5000 });
    } catch (e) {
      toast.error('Failed to repair EA names', { id: 'repair' });
    }
  };

  // Build full 30-day chart (fill missing days with 0)
  const chartData = (() => {
    const map = new Map();
    (stats?.revenueChart || []).forEach(r => map.set(r.date, r.amount));
    const result = [];
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 24 * 3600 * 1000);
      const key = d.toISOString().split('T')[0];
      result.push({ date: key.slice(5), amount: map.get(key) || 0 });
    }
    return result;
  })();

  return (
    <div>
      <AdminPageHeader
        title="Admin Dashboard"
        subtitle="Platform overview, revenue health, and safe maintenance actions"
      />

      {/* Top revenue cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-green-400/10 to-green-600/5 border border-dark-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign size={16} className="text-green-400" />
            <span className="text-xs text-gray-500 uppercase">Monthly Recurring Revenue</span>
          </div>
          <p className="text-3xl font-bold text-green-400">${stats?.mrr || 0}</p>
          <p className="text-xs text-gray-500 mt-1">${stats?.revenueThisMonth || 0} this month</p>
        </div>
        <div className="bg-gradient-to-br from-amber-400/10 to-amber-600/5 border border-dark-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard size={16} className="text-amber-400" />
            <span className="text-xs text-gray-500 uppercase">Active Subscriptions</span>
          </div>
          <p className="text-3xl font-bold text-amber-400">{stats?.activeSubscriptions || 0}</p>
          <p className="text-xs text-gray-500 mt-1">{stats?.proUsers || 0} pro users</p>
        </div>
        <Link to="/admin/payments" className="bg-gradient-to-br from-red-400/10 to-red-600/5 border border-dark-border rounded-xl p-5 hover:border-red-500/30 transition-all group">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={16} className="text-red-400" />
            <span className="text-xs text-gray-500 uppercase">Pending Payments</span>
            <ChevronRight size={14} className="text-red-400 ml-auto opacity-0 group-hover:opacity-100" />
          </div>
          <p className="text-3xl font-bold text-red-400">{stats?.pendingPayments || 0}</p>
          <p className="text-xs text-gray-500 mt-1">Click to review</p>
        </Link>
      </div>

      {/* Chart + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-dark-card border border-dark-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
            <TrendingUp size={14} className="text-emerald-400" /> Revenue (30 days)
          </h3>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData}>
              <XAxis dataKey="date" stroke="#666" fontSize={10} interval={4} />
              <YAxis stroke="#666" fontSize={10} />
              <Tooltip
                contentStyle={{ background: '#161b22', border: '1px solid #21262d', borderRadius: 8, fontSize: 12 }}
                formatter={(v) => [`$${v}`, 'Revenue']}
              />
              <Line type="monotone" dataKey="amount" stroke="#10b981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-dark-card border border-dark-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-200 mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <Link to="/admin/payments" className="flex items-center justify-between p-3 rounded-lg hover:bg-white/5 transition-all">
              <span className="text-sm text-gray-300">Review payments</span>
              {stats?.pendingPayments > 0 && (
                <span className="bg-red-500/20 text-red-400 text-xs px-2 py-0.5 rounded-full">{stats.pendingPayments}</span>
              )}
            </Link>
            <button
              onClick={() => setConfirmRepair(true)}
              className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-white/5 transition-all text-left"
            >
              <span className="text-sm text-gray-300">🔧 Repair EA names</span>
              <span className="bg-amber-500/20 text-amber-400 text-xs px-2 py-0.5 rounded-full">Fix</span>
            </button>
            <Link to="/admin/users" className="flex items-center justify-between p-3 rounded-lg hover:bg-white/5 transition-all">
              <span className="text-sm text-gray-300">Expiring soon</span>
              {stats?.expiringSoon > 0 && (
                <span className="bg-amber-500/20 text-amber-400 text-xs px-2 py-0.5 rounded-full">{stats.expiringSoon}</span>
              )}
            </Link>
            <Link to="/admin/users" className="flex items-center justify-between p-3 rounded-lg hover:bg-white/5 transition-all">
              <span className="text-sm text-gray-300">New signups (7d)</span>
              <span className="bg-blue-500/20 text-blue-400 text-xs px-2 py-0.5 rounded-full">{stats?.newUsers7d || 0}</span>
            </Link>
            <Link to="/admin/coupons" className="flex items-center justify-between p-3 rounded-lg hover:bg-white/5 transition-all">
              <span className="text-sm text-gray-300">Active users (7d)</span>
              <span className="bg-cyan-500/20 text-cyan-400 text-xs px-2 py-0.5 rounded-full">{stats?.activeUsers || 0}</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Platform stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total Users', value: stats?.totalUsers || 0, icon: Users, color: 'text-blue-400' },
          { label: 'Total Accounts', value: stats?.totalAccounts || 0, icon: BarChart3, color: 'text-emerald-400' },
          { label: 'Closed Trades', value: stats?.totalTrades || 0, icon: Activity, color: 'text-purple-400' },
          { label: 'New 30d', value: stats?.newUsers30d || 0, icon: UserPlus, color: 'text-cyan-400' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-dark-card border border-dark-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon size={14} className={color} />
              <span className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</span>
            </div>
            <p className={`text-xl font-bold ${color}`}>{value.toLocaleString()}</p>
          </div>
        ))}
      </div>

      {/* Recent activity */}
      <div className="bg-dark-card border border-dark-border rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2">
          <Clock size={14} /> Recent Activity
        </h3>
        {!stats?.recentActivity?.length ? (
          <p className="text-gray-500 text-sm text-center py-4">No recent activity</p>
        ) : (
          <div className="space-y-2">
            {stats.recentActivity.map((log, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-white/[0.02] text-sm">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${log.severity === 'error' ? 'bg-red-400' : log.severity === 'warning' ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                  <span className="text-gray-300 truncate">{log.message}</span>
                  {log.details && <span className="text-gray-600 text-xs truncate hidden md:inline">- {log.details}</span>}
                </div>
                <span className="text-gray-600 text-xs flex-shrink-0 ml-2">{log.created_at?.split('T')[0]}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <AdminConfirmDialog
        open={confirmRepair}
        tone="warning"
        title="Repair EA names?"
        message="This scans all trades and fixes missing or broker-system EA names while preserving user-renamed EAs."
        confirmLabel="Run Repair"
        onConfirm={repairEaNames}
        onCancel={() => setConfirmRepair(false)}
      />
    </div>
  );
}
